import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Search from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
} from "recharts";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";
import { MEDIDROP_COLORS, DEVICE_COLORS } from "@/config/colors";
import {
  PLATFORM,
  platformMeta,
  osBarColor,
  isKnownOsLabel,
  appStatusMeta,
  SAMPLE_DEVICE_INSIGHTS,
} from "@/utils/deviceInsightsUtils";

const ORANGE = MEDIDROP_COLORS.ORANGE;

// ── Summary stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, Icon, accent = ORANGE }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        position: "relative",
        overflow: "hidden",
        minHeight: 104,
      }}
    >
      {Icon && (
        <Icon sx={{ position: "absolute", right: -6, top: "50%", transform: "translateY(-50%)", fontSize: 92, color: accent, opacity: 0.1 }} />
      )}
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, color: accent, lineHeight: 1.1, fontSize: "2.1rem" }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: "text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

// ── Platform chip (reused in table) ───────────────────────────────────────────
function PlatformBadge({ platform }) {
  const m = platformMeta(platform);
  const Icon = platform === PLATFORM.ANDROID ? AndroidIcon : platform === PLATFORM.IOS ? AppleIcon : DevicesOtherIcon;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Icon sx={{ fontSize: 16, color: m.color }} />
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{m.label}</Typography>
    </Box>
  );
}

// Status pill — 'Up to date' / 'Update available' / Unknown (no version yet).
function StatusPill({ status }) {
  if (!status) {
    return (
      <Tooltip title="Waiting for data — app version not reported yet">
        <Typography variant="body2" sx={{ color: "text.disabled", fontStyle: "italic" }}>—</Typography>
      </Tooltip>
    );
  }
  const m = appStatusMeta(status);
  return (
    <Chip label={m.label} size="small" sx={{ height: 22, fontWeight: 700, fontSize: 11, backgroundColor: m.tint, color: m.color }} />
  );
}

const CARD_SX = {
  p: 2,
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const AdminDriverDevicesPage = () => {
  const [raw, setRaw] = useState(null);
  const [isSample, setIsSample] = useState(false);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(null);

  // filters
  const [platformFilter, setPlatformFilter] = useState("all"); // all | android | ios | outdated | oldapp
  const [pharmacyFilter, setPharmacyFilter] = useState("all");
  const [search, setSearch] = useState("");

  // app-version targets editor
  const [targets, setTargets] = useState({ ios: "", android: "" });
  const [savingTargets, setSavingTargets] = useState(false);
  const [targetMsg, setTargetMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phappsRequest(API_PATHS.ADMIN_FUNCTIONS_DEVICE_INSIGHTS, "GET");
      if (res && Array.isArray(res.drivers) && res.drivers.length > 0) {
        setRaw(res);
        setIsSample(false);
        setWarning(null);
      } else {
        setRaw(SAMPLE_DEVICE_INSIGHTS);
        setIsSample(true);
        setWarning(null);
      }
    } catch (e) {
      // Endpoint not deployed yet → show sample so the UI is reviewable.
      setRaw(SAMPLE_DEVICE_INSIGHTS);
      setIsSample(true);
      setWarning(getAdminApiErrorMessage(e, "Live device data unavailable — showing sample data."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sync the target editor whenever fresh data arrives.
  useEffect(() => {
    const t = raw?.appVersionTargets;
    if (t) setTargets({ ios: t.ios ?? "", android: t.android ?? "" });
  }, [raw]);

  const saveTargets = useCallback(async () => {
    setSavingTargets(true);
    setTargetMsg(null);
    try {
      const current = raw?.appVersionTargets ?? {};
      const updates = [];
      ["ios", "android"].forEach((p) => {
        const val = (targets[p] ?? "").trim();
        if (val && val !== (current[p] ?? "")) updates.push({ platform: p, latestVersion: val });
      });
      if (!updates.length) { setTargetMsg("No changes to save."); return; }
      for (const u of updates) {
        await phappsRequest(API_PATHS.ADMIN_FUNCTIONS_DEVICE_APP_VERSIONS, "POST", u);
      }
      setTargetMsg("Saved — recalculating statuses…");
      await load();
    } catch (e) {
      setTargetMsg(getAdminApiErrorMessage(e, "Failed to save target version."));
    } finally {
      setSavingTargets(false);
    }
  }, [raw, targets, load]);

  const summary = raw?.summary ?? { totalDrivers: 0, androidPct: 0, iosPct: 0, outdatedOsCount: 0, outdatedAppCount: 0 };
  const split = raw?.platformSplit ?? { android: 0, ios: 0, unknown: 0 };
  const osByVersion = raw?.osByVersion ?? [];
  const allDrivers = raw?.drivers ?? [];

  const pharmacies = useMemo(() => {
    const set = new Set();
    allDrivers.forEach((d) => { if (d.pharmacy) set.add(d.pharmacy); });
    return [...set].sort();
  }, [allDrivers]);

  const filteredDrivers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allDrivers.filter((d) => {
      if (platformFilter === "android" && d.platform !== PLATFORM.ANDROID) return false;
      if (platformFilter === "ios" && d.platform !== PLATFORM.IOS) return false;
      if (platformFilter === "outdated" && !d.isOutdatedOs) return false;
      if (platformFilter === "oldapp" && d.appUpToDate !== false) return false;
      if (pharmacyFilter !== "all" && d.pharmacy !== pharmacyFilter) return false;
      if (q) {
        const hay = `${d.name ?? ""} ${d.email ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allDrivers, platformFilter, pharmacyFilter, search]);

  const donutData = [
    { name: "Android", key: PLATFORM.ANDROID, value: split.android },
    { name: "iOS", key: PLATFORM.IOS, value: split.ios },
    ...(split.unknown ? [{ name: "Unknown", key: PLATFORM.UNKNOWN, value: split.unknown }] : []),
  ].filter((d) => d.value > 0);

  const rows = filteredDrivers.map((d, i) => ({ id: d.driverId ?? `r-${i}`, ...d }));

  const columns = [
    {
      field: "name",
      headerName: "Driver",
      flex: 1.2,
      minWidth: 160,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.value}</Typography>
          {p.row.email && <Typography variant="caption" sx={{ color: "text.secondary" }}>{p.row.email}</Typography>}
        </Box>
      ),
    },
    {
      field: "pharmacy",
      headerName: "Pharmacy",
      flex: 1,
      minWidth: 150,
      renderCell: (p) => (
        <Typography variant="body2" noWrap>
          {p.value ? getChemistName(p.value) : "—"}
          {p.row.pharmacyCount > 1 && (
            <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}> +{p.row.pharmacyCount - 1}</Typography>
          )}
        </Typography>
      ),
    },
    {
      field: "platform",
      headerName: "Platform",
      width: 120,
      renderCell: (p) => <PlatformBadge platform={p.value} />,
    },
    {
      field: "deviceName",
      headerName: "Device",
      flex: 1,
      minWidth: 150,
      renderCell: (p) => <Typography variant="body2" noWrap>{p.value || "—"}</Typography>,
    },
    {
      field: "osVersion",
      headerName: "OS Version",
      width: 160,
      renderCell: (p) => {
        const known = isKnownOsLabel(p.value);
        if (!known) {
          return (
            <Tooltip title="Waiting for data — driver hasn't refreshed on a build that reports OS version">
              <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>
            </Tooltip>
          );
        }
        return (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
              {p.value}
            </Typography>
            {p.row.isOutdatedOs && (
              <Chip label="Old" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: DEVICE_COLORS.OUTDATED_TINT, color: DEVICE_COLORS.OUTDATED }} />
            )}
          </Box>
        );
      },
    },
    {
      field: "appVersion",
      headerName: "App Ver.",
      width: 110,
      renderCell: (p) => (
        p.value ? (
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>{p.value}</Typography>
        ) : (
          <Tooltip title="Waiting for data — driver hasn't refreshed on a build that reports app version">
            <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>
          </Tooltip>
        )
      ),
    },
    {
      field: "appStatus",
      headerName: "Status",
      width: 140,
      sortable: true,
      sortComparator: (a, b) => {
        const rank = (s) => (s === "update_available" ? 0 : s === "up_to_date" ? 1 : 2);
        return rank(a) - rank(b);
      },
      renderCell: (p) => <StatusPill status={p.value} />,
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-4 w-full max-w-full">
        {/* Back link */}
        <Link
          to="/admin"
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          ← Admin
        </Link>

        {/* Header */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ color: ORANGE }}>
              Driver Devices
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Fleet-wide device &amp; OS breakdown · active in the last {raw?.activeWindowDays ?? 90} days
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading} sx={{ color: ORANGE }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {isSample && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Showing <strong>sample data</strong> — the live <code>AdminDeviceInsights</code> endpoint returned no data or is not yet deployed.
            {warning ? ` (${warning})` : ""}
          </Alert>
        )}

        {/* Summary cards — 5 across in a single line on desktop */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }, gap: 1.5 }}>
          <StatCard label="Total drivers" value={Number(summary.totalDrivers).toLocaleString()} Icon={GroupOutlinedIcon} accent={ORANGE} sub="active devices" />
          <StatCard label="Android" value={`${summary.androidPct}%`} Icon={AndroidIcon} accent={DEVICE_COLORS.ANDROID_DARK} sub={`${split.android} drivers`} />
          <StatCard label="iOS" value={`${summary.iosPct}%`} Icon={AppleIcon} accent={DEVICE_COLORS.IOS_DARK} sub={`${split.ios} drivers`} />
          <StatCard label="Outdated OS" value={Number(summary.outdatedOsCount).toLocaleString()} Icon={WarningAmberIcon} accent={DEVICE_COLORS.OUTDATED} sub={`< Android ${raw?.thresholds?.android ?? 13} · iOS ${raw?.thresholds?.ios ?? 16}`} />
          <StatCard label="Update available" value={Number(summary.outdatedAppCount ?? 0).toLocaleString()} Icon={SystemUpdateAltIcon} accent={DEVICE_COLORS.OUTDATED} sub="below latest app ver." />
        </Box>

        {/* App-version targets editor */}
        <Box sx={{ ...CARD_SX, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 1 }}>
            <SystemUpdateAltIcon sx={{ fontSize: 18, color: ORANGE }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#334155" }}>
              Latest app version targets
            </Typography>
            <Tooltip title="Drivers below this version for their platform show 'Update available'. Edit and save to recalculate.">
              <Typography variant="caption" sx={{ color: "text.secondary", cursor: "default" }}>ⓘ</Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AndroidIcon sx={{ fontSize: 16, color: DEVICE_COLORS.ANDROID_DARK }} />
            <TextField size="small" label="Android" value={targets.android}
              onChange={(e) => setTargets((t) => ({ ...t, android: e.target.value }))}
              sx={{ width: 110 }} inputProps={{ inputMode: "decimal" }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AppleIcon sx={{ fontSize: 16, color: DEVICE_COLORS.IOS_DARK }} />
            <TextField size="small" label="iOS" value={targets.ios}
              onChange={(e) => setTargets((t) => ({ ...t, ios: e.target.value }))}
              sx={{ width: 110 }} inputProps={{ inputMode: "decimal" }} />
          </Box>
          <Tooltip title={isSample ? "Connect the live endpoint to save targets" : ""}>
            <span>
              <Button variant="contained" size="small" onClick={saveTargets} disabled={savingTargets || isSample}
                sx={{ textTransform: "none", fontWeight: 700, backgroundColor: ORANGE, "&:hover": { backgroundColor: "#d4613c" } }}>
                {savingTargets ? "Saving…" : "Save"}
              </Button>
            </span>
          </Tooltip>
          {targetMsg && <Typography variant="caption" sx={{ color: "text.secondary" }}>{targetMsg}</Typography>}
        </Box>

        {/* Charts (left, stacked) + Per-driver table (right). Both columns stretch to
            the same height (default align stretch); the left column's stacked chart
            cards define that height and the table on the right fills to match, so the
            graph cards and the table card line up. */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 360px) 1fr" }, gap: 2, alignItems: "stretch" }}>
          {/* Left column: Platform Split + OS Version Spread stacked */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          {/* Platform split donut */}
          <Box sx={{ ...CARD_SX, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
              Platform split
            </Typography>
            <Box sx={{ flex: 1, display: "flex", gap: 2, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
              <Box sx={{ width: 200, height: 220, flexShrink: 0 }}>
                {/* Fixed-size chart (not ResponsiveContainer) — avoids the
                    Recharts zero-measure collapse inside the stretched flex column. */}
                <PieChart width={200} height={220}>
                  <Pie data={donutData} dataKey="value" nameKey="name" cx={100} cy={110} innerRadius={58} outerRadius={88} paddingAngle={2} stroke="none" isAnimationActive={false}>
                    {donutData.map((d) => <Cell key={d.key} fill={platformMeta(d.key).color} />)}
                  </Pie>
                  <ReTooltip formatter={(v, n) => [`${v} drivers`, n]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: 120 }}>
                {donutData.map((d) => (
                  <Box key={d.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: platformMeta(d.key).color }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 64 }}>{d.name}</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {d.value} · {summary.totalDrivers ? Math.round((d.value / (split.android + split.ios || 1)) * 100) : 0}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* OS version spread bar chart */}
          <Box sx={{ ...CARD_SX, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, textTransform: "uppercase", letterSpacing: 0.5 }}>
                OS version spread
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: DEVICE_COLORS.ANDROID }} />
                  <Typography variant="caption">Android</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: DEVICE_COLORS.IOS }} />
                  <Typography variant="caption">iOS</Typography>
                </Box>
              </Box>
            </Box>
            {/* Fixed pixel height — ResponsiveContainer needs a definite parent
                height or it measures 0 inside this flex column and renders nothing
                (same collapse the Platform Split donut avoids by going fixed-size).
                Width stays responsive since the card has a real width. */}
            <Box sx={{ height: 260, minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={osByVersion} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-18} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <ReTooltip formatter={(v) => [`${v} drivers`, "Count"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={46}>
                    {osByVersion.map((d, i) => <Cell key={i} fill={osBarColor(d.platform)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          </Box>{/* end left column */}

          {/* Right column: Per-driver table (fills the height of the two charts) */}
          <Box sx={{ ...CARD_SX, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Per-driver breakdown ({filteredDrivers.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={platformFilter}
                onChange={(_, v) => v && setPlatformFilter(v)}
                sx={{
                  "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5, borderColor: "#e5e7eb" },
                  "& .Mui-selected": { backgroundColor: `${ORANGE}1a !important`, color: `${ORANGE} !important`, borderColor: `${ORANGE} !important` },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="android">Android</ToggleButton>
                <ToggleButton value="ios">iOS</ToggleButton>
                <ToggleButton value="outdated">Outdated OS</ToggleButton>
                <ToggleButton value="oldapp">Old app version</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                size="small"
                select
                value={pharmacyFilter}
                onChange={(e) => setPharmacyFilter(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="all">All pharmacies</MenuItem>
                {pharmacies.map((p) => (
                  <MenuItem key={p} value={p}>{getChemistName(p)}</MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                placeholder="Search driver…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>) }}
                sx={{ minWidth: 220 }}
              />
            </Box>
          </Box>

          {/* Fill the remaining card height so the table card matches the stacked chart
              column beside it. The grid's internal scroll handles overflow, so the height
              stays fixed to the charts regardless of how many drivers match the filter. */}
          <Box sx={{ flex: 1, minHeight: 320, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              getRowHeight={() => 56}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "name", sort: "asc" }] },
              }}
              sx={{
                border: "none",
                height: "100%",
                "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f8fafc" },
                "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, color: "#475569" },
                "& .MuiDataGrid-cell": { borderColor: "#f1f5f9" },
              }}
            />
          </Box>
          </Box>{/* end right column */}
        </Box>{/* end charts+table grid */}
      </div>
    </div>
  );
};

export default AdminDriverDevicesPage;
